import { useCallback, useMemo, startTransition, useTransition } from "react";
import PropTypes from "prop-types";
import { useStore } from "zustand";
import { useNavigate } from "react-router";

import {
  Text,
  Button,
  ButtonGroup,
  ActionButton,
  MenuTrigger,
  Menu,
  MenuItem,
  DialogTrigger,
  AlertDialog,
  Popover,
  Divider,
  ProgressCircle,
} from "@react-spectrum/s2";

import { store } from "../../store";
import {
  useStory,
  useCreateStory,
  useDeleteStory,
  useGenerateImages,
} from "@app/hooks/stories";
import { useIsDevMode } from "@app/hooks/useRoles";

import { downloadStoryExcel } from "@app/services/storyExcel";
import { copyStoryOutline } from "@app/services/storyOutline";
import { getStoryPresentation } from "@app/services/storyAdobeExpress";
import { downloadFile, downloadImages } from "@app/services/downloadFile";
import { generatePresentation } from "@app/services/adobeExpress";
import { writeToClipboard } from "@app/services/writeToClipboard";

import { StoryShareDialog } from "./StoryShareDialog";
import { StoryStyleDialog } from "./StoryStyleDialog";
import { StoryPreviewDialog } from "./StoryPreviewDialog";
import { StoryBrandDialog } from "./StoryBrandDialog";

import DownloadIcon from "@react-spectrum/s2/icons/Download";
import TableIcon from "@react-spectrum/s2/icons/Table";
import DataIcon from "@react-spectrum/s2/icons/Data";
import CopyIcon from "@react-spectrum/s2/icons/Copy";
import ImagesIcon from "@react-spectrum/s2/icons/Images";
import DeleteIcon from "@react-spectrum/s2/icons/Delete";
import ShareIcon from "@react-spectrum/s2/icons/Share";
import MoreIcon from "@react-spectrum/s2/icons/More";
import SimilarIcon from "@react-spectrum/s2/icons/Similar";
import PreviewIcon from "@react-spectrum/s2/icons/Preview";
import BrandIcon from "@react-spectrum/s2/icons/Brand";
import AIGenerateIcon from "icon:@app/assets/AIGenerate.svg";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const StoryToolbar = ({ storyId }) => {
  if (!storyId) {
    return null;
  }

  const navigate = useNavigate();
  const isDevMode = useIsDevMode();

  const ims = useStore(store, (state) => state.ims);

  const storyQuery = useStory(storyId);
  const story = storyQuery.data;

  const createStory = useCreateStory();
  const generateImages = useGenerateImages(storyId);
  const deleteStory = useDeleteStory(storyId);

  // const isGenerating = useStore(store, (state) => state.isGenerating);
  const isPromptPersonasPending = useStore(
    store,
    (state) => state.isPromptPersonasPending,
  );
  const isPromptVisualsPending = useStore(
    store,
    (state) => state.isPromptVisualsPending,
  );
  const isGenerating = isPromptPersonasPending || isPromptVisualsPending;

  const hasVisuals = useMemo(
    () => !!story?.script?.some((s) => !!s.visuals?.length),
    [story],
  );

  console.log(storyId, {
    hasVisuals,
    isGenerating,
    isPromptPersonasPending,
    isPromptVisualsPending,
  });

  const generateStoryImages = useStore(
    store,
    (state) => state.generateStoryImages,
  );

  const handleDeleteStory = () => {
    deleteStory.mutate(
      {},
      {
        onSuccess: () => {
          store.getState().addToast(`Story deleted`, {
            variant: "positive",
            duration: 3000,
          });
          navigate("/stories");
        },
      },
    );
  };

  const isDuplicatePending = createStory.isPending;

  const handleDuplicateStory = () => {
    const { id, _id, title, ...copy } = story;

    createStory.mutate(
      {
        ...copy,
        id: self.crypto.randomUUID(),
        title: `${story.title} (Copy)`,
      },
      {
        onSuccess: (data) => {
          store.getState().addToast(`Story duplicated`, {
            variant: "positive",
            duration: 3000,
          });
          navigate(`/stories/${data.id}`);
        },
      },
    );
  };

  const handleGenerateSimilarStory = () => {
    store
      .getState()
      .loadWizardData(story.data, story.prompt, story.promptTemplate);
    store.getState().setPromptMinimized(false);
    navigate("/");
  };

  const handleGenerateImages = () => {
    generateImages.mutate({ forceGenerate: true });
  };

  const scriptColumns = useMemo(() => {
    if (!story?.script) {
      return [];
    }

    return [
      { id: "id", name: "No", allowsResizing: true, width: 80 },
      { id: "section", name: "Section", allowsResizing: true },
      {
        id: "title",
        name: "Title",
        isRowHeader: true,
        showDivider: true,
        allowsResizing: true,
      },
      { id: "text", name: "Paragraph Text", allowsResizing: true },
      { id: "visual", name: "Visual", allowsResizing: true },
      { id: "visualMessaging", name: "Messaging", allowsResizing: true },
      { id: "template", name: "Template", allowsResizing: true },
      { id: "personas", name: "Personas", allowsResizing: true },
      { id: "solutions", name: "Solutions", allowsResizing: true },
      { id: "comments", name: "Comments", allowsResizing: true },
    ];
  }, [story?.script]);

  const personasColumns = useMemo(() => {
    if (!story?.personas) {
      return [];
    }

    return [
      { id: "id", name: "No", allowsResizing: true, width: 80 },
      {
        id: "name",
        name: "Name",
        allowsResizing: true,
        isRowHeader: true,
        width: 120,
      },
      { id: "role", name: "Role", allowsResizing: true },
      { id: "description", name: "Description", allowsResizing: true },
      { id: "location", name: "Location", allowsResizing: true },
      { id: "age", name: "Age", allowsResizing: true, width: 100 },
    ];
  }, [story?.personas]);

  const storyImages = useMemo(() => {
    if (!story?.script || !story?.personas) {
      return [];
    }

    const scriptImages = story?.script?.flatMap(
      (item, idx) =>
        item?.visuals?.map((v) => ({
          ...v,
          name: `Script-${`${idx + 1}`.padStart(3, "0")}-${item?.title?.replace?.(/[^a-zA-Z0-9]/g, "-") ?? "_"}`,
        })) ?? [],
    );

    const personaImages = story?.personas?.flatMap((persona, idx) => ({
      url: persona?.avatarUrl,
      name: `Persona-${`${idx + 1}`.padStart(3, "0")}-${persona.name.replace(/[^a-zA-Z0-9]/g, "-")}`,
    }));

    return [...scriptImages, ...personaImages];
  }, [story?.script, story?.personas]);

  const handleStoryDownloadExcel = useCallback(() => {
    downloadStoryExcel({
      story: {
        ...story,
        script: {
          columns: scriptColumns,
          items: story.script,
        },
        personas: {
          columns: personasColumns,
          items: story.personas,
        },
      },
      user: ims.profile,
    });
  }, [ims, story, scriptColumns, personasColumns]);

  const [isDownloadingImages, startDownloadingImagesTransition] =
    useTransition();

  const handleStoryDownloadImages = useCallback(() => {
    startDownloadingImagesTransition(async () => {
      try {
        await downloadImages(
          storyImages,
          `be-story-images-${story.customer}-${story.industry}-${Date.now()}`,
        );
      } catch (error) {
        console.error("Error downloading images:", error);
      }
    });
  }, [storyImages, story]);

  const handleStoryDownloadJSON = useCallback(() => {
    downloadFile(
      JSON.stringify(story, null, 4),
      `be-story-${story.customer}-${story.industry}-${Date.now()}.json`,
      "application/json",
    );
  }, [story]);

  const handleStoryCopyOutline = useCallback(() => {
    copyStoryOutline(story)
      .then(() => {
        store.getState().addToast("Outline copied to clipboard", {
          variant: "positive",
          duration: 5000,
          actionLabel: "Open Express",
          shouldCloseOnAction: true,
          onAction: () => {
            window.open(
              "https://new.express.adobe.com/home/tools/generative-presentation",
              "_blank",
            );
          },
        });
        console.log("Outline copied to clipboard");
      })
      .catch((error) => {
        console.error("Error copying story outline:", error);
        store.getState().addToast("Error copying story outline", {
          variant: "negative",
          duration: 5000,
          actionLabel: "Retry",
          shouldCloseOnAction: true,
          onAction: () => {
            handleStoryCopyOutline();
          },
        });
      });
  }, [story]);

  const [isGeneratingPresentation, startGeneratingTransition] = useTransition();

  const handleGenPresentation = useCallback(() => {
    startGeneratingTransition(async () => {
      try {
        const presentationData = await getStoryPresentation({ ...story });
        console.log("Generating presentation with data:", presentationData);

        const responseData = await generatePresentation(
          presentationData,
          story.title,
        );
        console.log("Presentation generated successfully:", responseData);

        // await new Promise((resolve) => setTimeout(resolve, 3000));

        // const presentationId =
        //   "urn:aaid:sc:EU:006a6f75-e76b-4cc0-a246-20bb2c3a0d67";

        const projectUrl = new URL(
          `https://new.express.adobe.com/id/${responseData.docUrl}`,
        );

        projectUrl.searchParams.set("category", "brands");

        if (story?.brand?.name) {
          projectUrl.searchParams.set("q", story.brand.name);
        }

        // if (responseData?.docUrl) {
        store.getState().addToast("Presentation created", {
          variant: "positive",
          duration: 20000,
          actionLabel: "Open in Express",
          shouldCloseOnAction: true,
          onAction: () => {
            window.open(projectUrl, "_blank");
          },
        });
        // } else {
        //   throw new Error("No docUrl in response data", {
        //     cause: responseData,
        //   });
        // }
      } catch (error) {
        console.error("Error generating presentation:", error);
        store.getState().addToast("Error generating presentation", {
          variant: "negative",
          duration: 5000,
          actionLabel: "Retry",
          shouldCloseOnAction: true,
          onAction: () => {
            handleGenPresentation();
          },
        });
      }
    });
  }, [story]);

  const handleStoryCopyStructure = useCallback(() => {
    startTransition(async () => {
      const presentationData = await getStoryPresentation({ ...story });
      console.log("Generating presentation with data:", presentationData);

      await writeToClipboard({
        plain: JSON.stringify(presentationData, null, 2),
      });

      store.getState().addToast("Presentation data copied to clipboard", {
        variant: "positive",
        duration: 5000,
      });
    });
  }, [story]);

  // const handleWindowMessage = useCallback(
  //   (e) => {
  //     // console.log("window message:", e.data, e.source);
  //     if (e.data?.type === "DOCUMENT_LOADED") {
  //       console.log("!!!!!!!!!!!", "Express ready");
  //       handleGenPresentation();
  //     } else if (e.data?.type === "SUCCESSFUL_GENERATION") {
  //       console.log("!!!!!!!!!!!", "Presentation generated");
  //     }
  //   },
  //   [handleGenPresentation],
  // );

  // useEffect(() => {
  //   window.onmessage = handleWindowMessage;
  // }, [handleWindowMessage]);

  return (
    <div
      className={style({
        display: "flex",
        alignItems: "center",
        gap: 16,
        paddingX: 8,
        paddingY: 12,
        marginStart: "auto",
        borderRadius: "lg",
        backgroundColor: "elevated",
        boxSizing: "border-box",
        outlineStyle: "solid",
        outlineColor: "gray-200",
        outlineWidth: 1,
        outlineOffset: -1,
        boxShadow: "elevated",
        position: "sticky",
        top: 16,
        right: 16,
        zIndex: 1,
      })}
    >
      <ButtonGroup>
        <Button
          variant={hasVisuals && !isGenerating ? "genai" : "secondary"}
          isDisabled={isGeneratingPresentation}
          onPress={handleGenPresentation}
        >
          {isGeneratingPresentation ? (
            <ProgressCircle
              isIndeterminate
              size="S"
              staticColor="auto"
              styles={style({ alignSelf: "center", marginStart: -4 })}
            />
          ) : (
            <AIGenerateIcon />
          )}
          <Text>Create presentation</Text>
        </Button>
        <Divider orientation="vertical" />
        {!hasVisuals && !isGenerating && (
          <DialogTrigger>
            <ActionButton isDisabled={isGenerating}>
              <AIGenerateIcon />
              <Text>Generate images</Text>
            </ActionButton>
            <StoryStyleDialog
              storyId={storyId}
              onPrimaryAction={handleGenerateImages}
            />
          </DialogTrigger>
        )}
        <DialogTrigger>
          <ActionButton isDisabled={isGenerating}>
            <BrandIcon />
            <Text>Select brand</Text>
          </ActionButton>
          <StoryBrandDialog storyId={storyId} />
        </DialogTrigger>
        <DialogTrigger>
          <ActionButton isDisabled={isGenerating}>
            <PreviewIcon />
            <Text>Preview</Text>
          </ActionButton>
          <StoryPreviewDialog storyId={storyId} />
        </DialogTrigger>
      </ButtonGroup>
      <Divider orientation="vertical" />
      <MenuTrigger styles={style({ marginStart: "auto" })}>
        <ActionButton isQuiet isDisabled={isDownloadingImages}>
          {isDownloadingImages ? (
            <ProgressCircle
              isIndeterminate
              size="S"
              staticColor="auto"
              styles={style({ alignSelf: "center", marginStart: -4 })}
            />
          ) : (
            <DownloadIcon />
          )}
          <Text>Download</Text>
        </ActionButton>
        <Menu>
          <MenuItem onPress={handleStoryDownloadExcel}>
            <TableIcon />
            <Text>as Excel file</Text>
          </MenuItem>
          <MenuItem onPress={handleStoryDownloadJSON}>
            <DataIcon />
            <Text>as JSON</Text>
          </MenuItem>
          <MenuItem
            isDisabled={!hasVisuals}
            onPress={handleStoryDownloadImages}
          >
            <ImagesIcon />
            <Text>All Images</Text>
            <Text slot="description">ZIP file with all images</Text>
          </MenuItem>
          <MenuItem onPress={handleStoryCopyOutline}>
            <CopyIcon />
            <Text>Copy Outline</Text>
            <Text slot="description">
              Copy outline to clipboard
              <br />
              to use in Adobe Express
            </Text>
          </MenuItem>
          {isDevMode && (
            <MenuItem onPress={handleStoryCopyStructure}>
              <CopyIcon />
              <Text>Copy Presentation data</Text>
              <Text slot="description">
                Copy presentation data to clipboard
              </Text>
            </MenuItem>
          )}
        </Menu>
      </MenuTrigger>

      <DialogTrigger>
        <ActionButton aria-label="More options" isQuiet>
          <MoreIcon />
        </ActionButton>
        <Popover hideArrow={true} placement="bottom end">
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              gap: 2,
            })}
          >
            <DialogTrigger>
              <ActionButton
                isQuiet
                UNSAFE_style={{
                  justifyContent: "start",
                }}
              >
                <ShareIcon />
                <Text>Share</Text>
              </ActionButton>
              <StoryShareDialog story={story} />
            </DialogTrigger>
            <ActionButton
              isQuiet
              isDisabled={isDuplicatePending}
              onPress={handleDuplicateStory}
              UNSAFE_style={{
                justifyContent: "start",
              }}
            >
              {isDuplicatePending ? (
                <ProgressCircle
                  isIndeterminate
                  size="S"
                  staticColor="auto"
                  styles={style({ alignSelf: "center", marginStart: -4 })}
                />
              ) : (
                <CopyIcon />
              )}
              <Text>Duplicate</Text>
            </ActionButton>
            {!!story?.promptTemplate && (
              <ActionButton
                isQuiet
                UNSAFE_style={{
                  justifyContent: "start",
                }}
                onPress={handleGenerateSimilarStory}
              >
                <SimilarIcon />
                <Text>Generate similar</Text>
              </ActionButton>
            )}
            {hasVisuals && (
              <DialogTrigger>
                <ActionButton isQuiet isDisabled={isGenerating}>
                  <AIGenerateIcon />
                  <Text>Re-generate images</Text>
                </ActionButton>
                <StoryStyleDialog
                  storyId={storyId}
                  onPrimaryAction={handleGenerateImages}
                />
              </DialogTrigger>
            )}
            <DialogTrigger>
              <ActionButton
                isQuiet
                isDisabled={deleteStory.isPending}
                UNSAFE_className={style({ color: "negative" })}
                UNSAFE_style={{
                  justifyContent: "start",
                }}
              >
                {deleteStory.isPending ? (
                  <ProgressCircle
                    isIndeterminate
                    size="S"
                    staticColor="auto"
                    styles={style({ alignSelf: "center", marginStart: -4 })}
                  />
                ) : (
                  <DeleteIcon />
                )}
                <Text>Delete story</Text>
              </ActionButton>
              <AlertDialog
                title="Delete Story"
                variant="destructive"
                primaryActionLabel="Delete"
                cancelLabel="Cancel"
                onPrimaryAction={handleDeleteStory}
              >
                <Text>
                  Are you sure you want to delete {story?.title ?? "this"}{" "}
                  story?
                </Text>
              </AlertDialog>
            </DialogTrigger>
          </div>
        </Popover>
      </DialogTrigger>
    </div>
  );
};

StoryToolbar.propTypes = {
  storyId: PropTypes.string,
};
